import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  Sparkles, Eye, Play, Film, Image as ImageIcon, X, 
  ChevronLeft, ChevronRight, Calendar, Maximize2, Volume2, 
  Scissors, Award, Flame, CheckCircle2 
} from 'lucide-react';
import { galleryCategories } from '../data/gallery';
import { getStoredGallery, fetchGalleryFromServer } from '../utils/storage';
import { useLanguage } from '../context/LanguageContext';

export default function Gallery({ onOpenBooking }) {
  const { lang, t } = useLanguage();
  const [items, setItems] = useState(getStoredGallery());
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);

  useEffect(() => {
    if (selectedMediaIndex !== null) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedMediaIndex]);

  useEffect(() => {
    fetchGalleryFromServer().then(setItems);

    const handleUpdate = () => {
      setItems(getStoredGallery());
    };
    window.addEventListener('aurahair_data_change', handleUpdate);
    return () => window.removeEventListener('aurahair_data_change', handleUpdate);
  }, []);

  const filteredItems = items.filter(item => {
    if (activeCategory === "all") return true;
    if (activeCategory === "photo") return item.type !== 'video';
    if (activeCategory === "video") return item.type === 'video';
    return item.category === activeCategory;
  });

  const selectedMedia = selectedMediaIndex !== null ? filteredItems[selectedMediaIndex] : null;

  const handleOpenMedia = (item) => {
    const idx = filteredItems.findIndex(i => i.id === item.id);
    setSelectedMediaIndex(idx !== -1 ? idx : 0);
  };

  const handleNextMedia = useCallback(() => {
    if (filteredItems.length === 0) return;
    setSelectedMediaIndex((prev) => (prev + 1) % filteredItems.length);
  }, [filteredItems.length]);

  const handlePrevMedia = useCallback(() => {
    if (filteredItems.length === 0) return;
    setSelectedMediaIndex((prev) => (prev - 1 + filteredItems.length) % filteredItems.length);
  }, [filteredItems.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedMediaIndex === null) return;
      if (e.key === 'ArrowRight') handleNextMedia();
      if (e.key === 'ArrowLeft') handlePrevMedia();
      if (e.key === 'Escape') setSelectedMediaIndex(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedMediaIndex, handleNextMedia, handlePrevMedia]);

  const getEmbedVideoUrl = (url) => {
    if (!url) return null;
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
    }
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
    }
    return null;
  };

  // Category counts
  const getCategoryCount = (catId) => {
    if (catId === 'all') return items.length;
    if (catId === 'photo') return items.filter(i => i.type !== 'video').length;
    if (catId === 'video') return items.filter(i => i.type === 'video').length;
    return items.filter(i => i.category === catId).length;
  };

  return (
    <section id="galeri" className="py-20 sm:py-28 relative bg-slate-950 text-white overflow-hidden w-full select-none">
      
      {/* 🌌 Animated Golden Aurora & Glowing Ambient Lights */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-to-tr from-amber-500/10 via-yellow-500/5 to-transparent rounded-full blur-[120px] pointer-events-none animate-gold-pulse" />
      <div className="absolute bottom-10 -left-20 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -right-20 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* ================= SECTION HEADER ================= */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-300 text-xs font-black uppercase tracking-widest mb-4 shadow-lg shadow-amber-500/10 backdrop-blur-md">
            <Sparkles className="w-3.5 h-3.5 text-amber-400 animate-spin" style={{ animationDuration: '6s' }} />
            <span>AURA HAIR ROTTERDAM • MASTERPIECE GALLERY</span>
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping" />
          </div>

          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black font-serif tracking-tight text-white leading-tight">
            {lang === 'nl' ? 'Exclusieve' : lang === 'tr' ? 'Kusursuz' : 'Exclusive'}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500">
              {lang === 'nl' ? "Haartransformaties & 4K Video's" : lang === 'tr' ? 'Saç Sanatı & 4K Videolar' : 'Hair Art & 4K Videos'}
            </span>
          </h2>

          <p className="mt-4 text-sm sm:text-base text-slate-400 font-medium leading-relaxed max-w-2xl mx-auto">
            {lang === 'nl'
              ? "Bekijk adembenemende voor-en-na resultaten, master skin fades en 4K salon video's rechtstreeks uit Rotterdam."
              : lang === 'tr'
                ? 'Rotterdam salonumuzdan jilet gibi fade geçişleri, ameliyatsız protez saç dönüşümleri ve 4K ultra HD videolar.'
                : 'Experience breath-taking hair systems, master skin fades and 4K ultra-HD transformations in Rotterdam.'}
          </p>
        </div>

        {/* ================= CATEGORY FILTER TABS ================= */}
        <div className="flex items-center justify-start sm:justify-center overflow-x-auto no-scrollbar scroll-smooth gap-2 pb-2 mb-12">
          {galleryCategories.map((cat) => {
            const isSelected = activeCategory === cat.id;
            const catName = typeof cat.name === 'object' ? (cat.name[lang] || cat.name.nl) : cat.name;
            const count = getCategoryCount(cat.id);

            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`group relative px-4 py-2.5 rounded-2xl text-xs sm:text-sm font-black transition-all duration-300 shrink-0 cursor-pointer flex items-center gap-2 border ${
                  isSelected
                    ? 'bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-slate-950 border-amber-300 shadow-xl shadow-amber-500/25 scale-105'
                    : 'bg-slate-900/80 hover:bg-slate-800 text-slate-300 hover:text-white border-slate-800 hover:border-slate-700'
                }`}
              >
                <span>{catName}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-black transition-colors ${
                  isSelected 
                    ? 'bg-slate-950 text-amber-300' 
                    : 'bg-slate-800 text-slate-400 group-hover:text-amber-300'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* ================= 3D ANIMATED GALLERY GRID ================= */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-7">
          {filteredItems.map((item, idx) => {
            const title = typeof item.title === 'object' ? (item.title[lang] || item.title.nl) : item.title;
            const isVideo = item.type === 'video' || Boolean(item.videoUrl);

            return (
              <div
                key={item.id}
                onClick={() => handleOpenMedia(item)}
                className="group relative rounded-3xl overflow-hidden cursor-pointer bg-slate-900 border border-slate-800 hover:border-amber-400/80 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_45px_-10px_rgba(245,158,11,0.35)] aspect-[4/5] flex flex-col justify-end"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                {/* 🌟 Glowing Halo Behind Card on Hover */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-3xl blur-md opacity-0 group-hover:opacity-60 transition duration-500 -z-10" />

                {/* Shimmer Sweep Light Animation */}
                <div className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent z-20" />

                {/* Media Image / Video Poster */}
                <img
                  src={item.image || "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?w=800&auto=format&fit=crop&q=80"}
                  alt={title}
                  className="absolute inset-0 w-full h-full object-cover scale-100 group-hover:scale-110 transition-transform duration-1000 ease-out"
                  loading="lazy"
                />

                {/* Cinematic Dark Vignette Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent group-hover:from-slate-950 group-hover:via-slate-950/50 transition-all duration-500" />

                {/* Top Badge Overlay */}
                <div className="absolute top-3.5 left-3.5 right-3.5 flex items-center justify-between z-20">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-slate-950/85 border border-amber-400/40 text-amber-300 text-[11px] font-black uppercase tracking-wider backdrop-blur-md shadow-md">
                    {isVideo ? (
                      <>
                        <span className="w-2 h-2 rounded-full bg-rose-500 animate-ping mr-0.5" />
                        <Film className="w-3.5 h-3.5 text-rose-400" />
                        <span>4K VİDEO</span>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-3.5 h-3.5 text-amber-400" />
                        <span>{item.tag || "HD FOTO"}</span>
                      </>
                    )}
                  </div>

                  {isVideo && item.duration && (
                    <div className="px-2.5 py-1 rounded-xl bg-slate-950/85 border border-slate-700 text-white font-mono text-[10px] font-black backdrop-blur-md">
                      {item.duration}
                    </div>
                  )}
                </div>

                {/* 🎥 Center Video Radar Play Badge */}
                {isVideo && (
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <div className="relative flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-amber-500/25 animate-ping absolute" />
                      <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-amber-500 via-yellow-400 to-amber-500 text-slate-950 flex items-center justify-center shadow-xl shadow-amber-500/50 group-hover:scale-125 transition-transform duration-300 ring-4 ring-amber-400/40">
                        <Play className="w-6 h-6 fill-current ml-0.5" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Soundwave bars jumping on video hover */}
                {isVideo && (
                  <div className="absolute bottom-16 right-4 z-20 flex items-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="w-1 bg-amber-400 rounded-full wave-bar-1" />
                    <span className="w-1 bg-amber-400 rounded-full wave-bar-2" />
                    <span className="w-1 bg-amber-400 rounded-full wave-bar-3" />
                    <span className="w-1 bg-amber-400 rounded-full wave-bar-4" />
                  </div>
                )}

                {/* Bottom Glassmorphic Caption Drawer */}
                <div className="relative z-20 p-5 transform translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                  <div className="text-[10px] font-bold text-amber-400 uppercase tracking-widest mb-1 opacity-90">
                    Aura Hair Rotterdam
                  </div>
                  <h3 className="text-sm sm:text-base font-black text-white leading-snug drop-shadow-md line-clamp-2 group-hover:text-amber-200 transition-colors">
                    {title}
                  </h3>
                  
                  <div className="mt-3 flex items-center justify-between text-xs font-black text-amber-400">
                    <span className="inline-flex items-center gap-1.5 group-hover:translate-x-1 transition-transform">
                      {isVideo ? <Play className="w-3.5 h-3.5 fill-current" /> : <Eye className="w-3.5 h-3.5" />}
                      <span>{isVideo ? 'Videoyu 4K İzle' : 'Fotoğrafı Büyüt'}</span>
                    </span>
                    <Maximize2 className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                  </div>
                </div>

              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="text-center py-20 bg-slate-900/60 rounded-3xl border border-slate-800 max-w-md mx-auto">
            <Film className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 font-bold">Bu kategoride henüz medya bulunmuyor.</p>
          </div>
        )}

      </div>

      {/* ================= LUXURY CAROUSEL LIGHTBOX MODAL (PORTAL) ================= */}
      {selectedMedia && typeof document !== 'undefined' && createPortal(
        <div
          onClick={() => setSelectedMediaIndex(null)}
          className="fixed inset-0 z-[100000] bg-black/95 backdrop-blur-xl flex items-center justify-center p-2 sm:p-6 animate-fade-in"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-5xl w-full bg-slate-950 rounded-3xl overflow-hidden border-2 border-amber-500/40 shadow-2xl flex flex-col my-auto max-h-[96vh]"
          >
            {/* Top Modal Controls */}
            <div className="px-5 py-3.5 bg-slate-900/90 border-b border-slate-800 flex items-center justify-between z-30">
              <div className="flex items-center gap-3">
                <span className="px-3 py-1 rounded-xl bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-black uppercase tracking-wider">
                  {selectedMedia.type === 'video' ? '🎥 4K Video Oynatıcı' : '📸 HD Fotoğraf Görüntüleyici'}
                </span>
                <span className="text-xs font-mono font-bold text-slate-400">
                  {selectedMediaIndex + 1} / {filteredItems.length}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedMediaIndex(null)}
                  aria-label="Kapat"
                  className="w-9 h-9 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Media Presentation Container */}
            <div className="relative w-full bg-black flex items-center justify-center overflow-hidden min-h-[300px] max-h-[72vh]">
              
              {/* Previous Button Arrow */}
              {filteredItems.length > 1 && (
                <button
                  type="button"
                  onClick={handlePrevMedia}
                  aria-label="Önceki"
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-2xl bg-black/60 hover:bg-amber-500 text-white hover:text-slate-950 border border-slate-700 hover:border-amber-400 flex items-center justify-center transition-all duration-200 cursor-pointer shadow-xl backdrop-blur-md hover:scale-110 active:scale-95"
                >
                  <ChevronLeft className="w-6 h-6 stroke-[3]" />
                </button>
              )}

              {/* Next Button Arrow */}
              {filteredItems.length > 1 && (
                <button
                  type="button"
                  onClick={handleNextMedia}
                  aria-label="Sonraki"
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-2xl bg-black/60 hover:bg-amber-500 text-white hover:text-slate-950 border border-slate-700 hover:border-amber-400 flex items-center justify-center transition-all duration-200 cursor-pointer shadow-xl backdrop-blur-md hover:scale-110 active:scale-95"
                >
                  <ChevronRight className="w-6 h-6 stroke-[3]" />
                </button>
              )}

              {/* Media Content */}
              {selectedMedia.type === 'video' || selectedMedia.videoUrl ? (
                getEmbedVideoUrl(selectedMedia.videoUrl) ? (
                  <iframe
                    src={getEmbedVideoUrl(selectedMedia.videoUrl)}
                    title={typeof selectedMedia.title === 'object' ? (selectedMedia.title[lang] || selectedMedia.title.nl) : selectedMedia.title}
                    className="w-full aspect-video border-0 max-h-[70vh]"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={selectedMedia.videoUrl}
                    controls
                    autoPlay
                    playsInline
                    className="w-full max-h-[70vh] object-contain"
                  >
                    Tarayıcınız video oynatmayı desteklemiyor.
                  </video>
                )
              ) : (
                <img
                  src={selectedMedia.image}
                  alt={typeof selectedMedia.title === 'object' ? (selectedMedia.title[lang] || selectedMedia.title.nl) : selectedMedia.title}
                  className="w-full max-h-[70vh] object-contain select-none transition-transform duration-300"
                />
              )}
            </div>

            {/* Bottom Information & Booking Action Bar */}
            <div className="p-4 sm:p-5 bg-slate-900 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-white">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-amber-400 uppercase tracking-wider">
                    {selectedMedia.tag || (selectedMedia.type === 'video' ? 'Video 4K' : 'Master Style')}
                  </span>
                  {selectedMedia.duration && (
                    <span className="text-[11px] font-mono text-slate-400">
                      • Süre: {selectedMedia.duration}
                    </span>
                  )}
                </div>
                <h4 className="text-base sm:text-lg font-black text-white mt-0.5">
                  {typeof selectedMedia.title === 'object' ? (selectedMedia.title[lang] || selectedMedia.title.nl) : selectedMedia.title}
                </h4>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 shrink-0">
                {onOpenBooking && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMediaIndex(null);
                      onOpenBooking();
                    }}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-amber-500/25 transition-all flex items-center gap-1.5 cursor-pointer hover:scale-105 active:scale-95"
                  >
                    <Calendar className="w-3.5 h-3.5 stroke-[2.5]" />
                    <span>Bu Stili Randevu Al</span>
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={() => setSelectedMediaIndex(null)}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs transition-colors cursor-pointer"
                >
                  Kapat
                </button>
              </div>
            </div>

          </div>
        </div>,
        document.body
      )}

    </section>
  );
}
